import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

// ProductRecord + useProducts live in ./products now (Phase 1D.4).
// Re-export for backwards compatibility with consumers that already
// imported them from this module.
export { useProducts } from "./products";
export type { ProductRecord } from "./products";

export type LicenseStatus = "active" | "suspended" | "expired" | "revoked";

export interface LicenseRecord {
    id: string;
    tenantId: string;
    productSlug: string;
    name: string;
    status: LicenseStatus;
    iocTypes: string[];
    detectionScopes: Record<string, string[]>;
    quotaPer10m?: number;
    quotaPerHour?: number;
    quotaPer6h?: number;
    quotaPerDay?: number;
    quotaPerWeek?: number;
    quotaTotal?: number;
    seats?: number;
    devices?: number;
    expiresAt?: string;
    validFrom?: string;
    country?: string;
    notes?: string;
    primaryContactUserId?: string;
    apiKeyCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface ApiKeyRecord {
    id: string;
    keyPrefix: string;
    label: string;
    licenseId: string;
    scopes: string[];
    createdBy?: string;
    createdAt: string;
    expiresAt?: string;
    lastUsedAt?: string;
    revokedAt?: string;
}

/**
 * Canonical IoC (observable) types — used by the /iocs filter and by license
 * grants that scope which observables a product can query. Intelligence
 * objects (CVE, APT group, campaign, MITRE TTP, threat report) are NOT
 * IoCs; they live on their own pages (`/cves` today) and must not mingle
 * with observables in filter pickers.
 */
export const IOC_TYPES: ReadonlyArray<{ slug: string; label: string }> = [
    { slug: "ip", label: "IP" },
    { slug: "cidr", label: "CIDR" },
    { slug: "domain", label: "Domain" },
    { slug: "email", label: "Email" },
    { slug: "url", label: "URL" },
    { slug: "certificate", label: "Certificate" },
    { slug: "hash", label: "Hash" },
];

/**
 * DETECTION_SCOPE_CAPABILITY_MAP — mirror of
 * internal/license/detection_scopes.go:DetectionScopeCapabilityMap.
 *
 * Phase 2.O REDUX: YARA scopes are keyed on product_target
 * (dmx/dsx/dfx/any) rather than scan_context. Each grant maps 1:1 to a
 * product capability slug of the same name; `any` maps to the conservative
 * dsx capability (tenants without dsx simply never route through `any`).
 *
 * Sigma/Suricata/Elastic/Custom keep the wildcard-only posture (empty
 * per-scope map) until per-product fan-out lights up.
 */
export const DETECTION_SCOPE_CAPABILITY_MAP: Readonly<
    Record<string, Readonly<Record<string, string>>>
> = {
    yara: {
        dmx: "dmx",
        dsx: "dsx",
        dfx: "dfx",
        any: "dsx",
    },
    sigma: {},
    suricata: {},
    elastic: {},
    custom: {},
};

/**
 * DETECTION_RULE_TYPES is the canonical render order for the matrix.
 *
 * Phase 2.O REDUX: the YARA row carries product_targets instead of
 * scan_contexts. The `any` grant is surfaced separately so operators can
 * opt into cross-product fan-out when they intentionally want it.
 */
export const DETECTION_RULE_TYPES: ReadonlyArray<{
    slug: string;
    label: string;
    contexts: ReadonlyArray<{ slug: string; label: string }>;
}> = [
    {
        slug: "yara",
        label: "YARA",
        contexts: [
            { slug: "dmx", label: "DMX" },
            { slug: "dsx", label: "DSX" },
            { slug: "dfx", label: "DFX" },
            { slug: "any", label: "Any (fan-out)" },
        ],
    },
    { slug: "sigma", label: "Sigma", contexts: [{ slug: "all", label: "All" }] },
    { slug: "suricata", label: "Suricata", contexts: [{ slug: "all", label: "All" }] },
    { slug: "elastic", label: "Elastic", contexts: [{ slug: "all", label: "All" }] },
    { slug: "custom", label: "Custom", contexts: [{ slug: "all", label: "All" }] },
];

/**
 * productSupportsDetectionScope — true when (ruleType, scope) is grantable
 * given the product's capability set. The wildcard "all" is always
 * grantable (server enforces only the typed entries).
 *
 * Phase 2.O REDUX: for YARA the `scope` parameter is a product_target
 * string (dmx/dsx/dfx/any) rather than a scan_context.
 */
export function productSupportsDetectionScope(
    productCapabilities: readonly string[],
    ruleType: string,
    scope: string,
): boolean {
    if (scope === "all") return true;
    const ctxMap = DETECTION_SCOPE_CAPABILITY_MAP[ruleType];
    if (!ctxMap) return false;
    const need = ctxMap[scope];
    if (!need) return false;
    return productCapabilities.includes(need);
}

const LICENSES_KEY = ["admin", "licenses"] as const;
const KEYS_KEY = (licenseId: string) => ["admin", "licenses", licenseId, "keys"] as const;

export function useLicenses() {
    return useQuery({
        queryKey: LICENSES_KEY,
        queryFn: () => api.get<{ licenses: LicenseRecord[] }>("/api/v2/licenses").then((d) => d.licenses),
        staleTime: 10_000,
    });
}

export interface CreateLicenseInput {
    tenantId: string;
    productSlug: string;
    name: string;
    iocTypes?: string[];
    detectionScopes?: Record<string, string[]>;
    quotaPer10m?: number;
    quotaPerHour?: number;
    quotaPer6h?: number;
    quotaPerDay?: number;
    quotaPerWeek?: number;
    quotaTotal?: number;
    seats?: number;
    devices?: number;
    expiresAt?: string; // RFC3339
    validFrom?: string; // RFC3339
    country?: string;
    notes?: string;
    primaryContactUserId?: string;
}

export function useCreateLicense() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateLicenseInput) =>
            api.post<{ license: LicenseRecord }>("/api/v2/licenses", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: LICENSES_KEY });
        },
    });
}

export interface UpdateLicenseInput {
    id: string;
    name?: string;
    status?: LicenseStatus;
    iocTypes?: string[];
    detectionScopes?: Record<string, string[]>;
    quotaPer10m?: number;
    quotaPerHour?: number;
    quotaPer6h?: number;
    quotaPerDay?: number;
    quotaPerWeek?: number;
    quotaTotal?: number;
    seats?: number;
    devices?: number;
    expiresAt?: string;
    validFrom?: string;
    country?: string;
    notes?: string;
    primaryContactUserId?: string;
    clearPrimaryContact?: boolean;
}

export function useUpdateLicense() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...patch }: UpdateLicenseInput) =>
            api.patch<{ license: LicenseRecord }>(`/api/v2/licenses/${id}`, patch),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: LICENSES_KEY });
            void qc.invalidateQueries({ queryKey: KEYS_KEY(variables.id) });
        },
    });
}

export function useDeleteLicense() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete<void>(`/api/v2/licenses/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: LICENSES_KEY });
        },
    });
}

// ---- API keys ---------------------------------------------------------

export function useApiKeys(licenseId: string | undefined) {
    return useQuery({
        queryKey: licenseId ? KEYS_KEY(licenseId) : ["admin", "licenses", "none", "keys"],
        queryFn: () =>
            api.get<{ keys: ApiKeyRecord[] }>(`/api/v2/licenses/${licenseId}/keys`).then((d) => d.keys),
        enabled: !!licenseId,
    });
}

interface IssueApiKeyInput {
    licenseId: string;
    label: string;
    scopes?: string[];
    expiresAt?: string;
}

export interface IssuedApiKey {
    key: ApiKeyRecord;
    secret: string; // shown once
}

export function useIssueApiKey() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ licenseId, ...body }: IssueApiKeyInput) =>
            api.post<IssuedApiKey>(`/api/v2/licenses/${licenseId}/keys`, body),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: KEYS_KEY(variables.licenseId) });
            void qc.invalidateQueries({ queryKey: LICENSES_KEY });
        },
    });
}

export function useRevokeApiKey(licenseId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (keyId: string) =>
            api.post<void>(`/api/v2/api-keys/${keyId}/revoke`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: KEYS_KEY(licenseId) });
            void qc.invalidateQueries({ queryKey: LICENSES_KEY });
        },
    });
}

// ---- Legacy keys (V0/V1 portal bridge) -------------------------------------
//
// One license can carry zero-or-more legacy keys attached during the
// import-legacy-licenses run. The full plaintext key never reaches the
// browser — backend stores sha256(uuid) + last4 and the dialog renders
// `••••<last4>` chips. Operators can revoke a single legacy key without
// touching the parent V2 license, or hard-delete it (cleanup) once the
// customer has rotated to a V2-issued `irk_…` key.

export interface LegacyKeyRecord {
    id: string;
    licenseId: string;
    last4: string;
    sourceSystem: "v0_portal" | "v1_portal";
    legacyProduct: string;
    legacyStatus: string;
    legacyUserId?: string;
    legacyUserLabel: string;
    activationLimit: number;
    usageCount: number;
    fingerprints: string[];
    unlimited: boolean;
    validFrom: string;
    validUntil: string;
    revokedAt?: string;
    importedAt: string;
}

const LEGACY_KEYS_KEY = (licenseId: string) =>
    ["admin", "licenses", licenseId, "legacy-keys"] as const;

export function useLegacyKeys(licenseId: string | undefined) {
    return useQuery({
        queryKey: licenseId
            ? LEGACY_KEYS_KEY(licenseId)
            : ["admin", "licenses", "none", "legacy-keys"],
        queryFn: () =>
            api
                .get<{ keys: LegacyKeyRecord[] }>(
                    `/api/v2/licenses/${licenseId}/legacy-keys`,
                )
                .then((d) => d.keys),
        enabled: !!licenseId,
    });
}

export function useRevokeLegacyKey(licenseId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (keyId: string) =>
            api.post<{ revoked?: boolean; alreadyRevoked?: boolean }>(
                `/api/v2/licenses/${licenseId}/legacy-keys/${keyId}/revoke`,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: LEGACY_KEYS_KEY(licenseId) });
        },
    });
}

export function useDeleteLegacyKey(licenseId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (keyId: string) =>
            api.delete<void>(
                `/api/v2/licenses/${licenseId}/legacy-keys/${keyId}`,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: LEGACY_KEYS_KEY(licenseId) });
        },
    });
}
